import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@shared/components/Button';
import { DropTarget } from '@shared/components/DropTarget/DropTarget';
import { Modal } from '@shared/components/Modal';
import { MBFileDropListener } from '@shared/components/form/fileDropListener';
import { MBIconV2 } from '@shared/components/image';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { getController, setControlledTimeout } from '@shared/util/timeout';
import { AskScope } from '../api';
import { AskAttachmentTray } from '../components/AskAttachmentTray';
import { AskCameraButton } from '../components/AskCameraButton';
import { AskDictateButton } from '../components/AskDictateButton';
import { AskDictationBar } from '../components/AskDictationBar';
import { ASK_MARK_ICON } from '../components/AskMark';
import { AskRecordButton } from '../components/AskRecordButton';
import { SPARKLE_COLLAPSE_MS, SparkleBackdrop } from '../components/SparkleBackdrop';
import { useAskAttachmentUpload, useLocalAskAttachments } from '../upload';
import { useAsk } from '../useAsk';
import { ASK_ATTACHMENT_ACCEPT, ASK_MAX_ATTACHMENTS } from '../util/askAttachmentTypes';
import { ASK_VOICE_INPUT_MODE, useAskDictation } from '../voice';
import { CaptureStatus, useCapture } from './useCapture';
import { useReceiveCaptureFiles } from './useReceiveCaptureFiles';
import styles from './CaptureModal.module.css';

/**
 * A step up from the composer's trio, and still not the same number as each other.
 *
 * The composer sizes these against a 5Q input pill among a row of other controls. Here they are the
 * three things the screen is for, so all three grow. They stay unequal for the same reason as in the
 * composer: the attach glyph fills its box while the microphone is a thinner outline with air around
 * it, so matching them by pixel size makes the attach button look like the bigger of the two — and
 * the camera, whose body is the widest of the three, is set below both for the same reason.
 */
const ATTACH_ICON_SIZE = grid(3.75);
const CAMERA_ICON_SIZE = grid(3.625);
const MICROPHONE_ICON_SIZE = grid(4);

/**
 * The mark beside the hint. Larger than the one in the Capture button, and larger than the copy it
 * sits next to: the glyph is thin outlined strokes with transparent margin inside its own viewBox, so
 * it reads a size smaller than it measures, and this is the one place on the surface that says what
 * fills the form in.
 */
const HINT_MARK_SIZE = grid(5);

interface CaptureModalProps {
  workflowTemplateId: string | null | undefined;
  channelSlug: string;
  scope: AskScope;
  onClose: () => void;
}

/**
 * Collects a photo, a document or a dictation, and fills the create form from it.
 *
 * Assembled from the composer's own controls rather than from copies: the same attach button, the
 * same camera, the same microphone, the same dictation bar, the same tray, the same upload pipeline
 * — so the content-type policy and the free-plan rule are enforced once and the two surfaces cannot
 * drift.
 * What differs is where the files live (this modal, not the conversation) and what happens to the
 * answer (the form, not a transcript).
 */
export function CaptureModal({
  workflowTemplateId,
  channelSlug,
  scope,
  onClose,
}: CaptureModalProps) {
  const { close: closeAskPanel } = useAsk();
  const store = useLocalAskAttachments();
  const upload = useAskAttachmentUpload(store);
  const { attachments, removeAttachment } = store;
  const { pickAttachments, addDroppedFiles, addRecording, retryAttachment, rejection } = upload;
  const { status, isReady, isResolving, run } = useCapture({
    workflowTemplateId,
    channelSlug,
    scope,
  });

  const [text, setText] = useState('');
  const [isCollapsing, setIsCollapsing] = useState(false);
  const inputRef = useRef<MBAutosizeTextInput>(null);

  // The field belongs to the run and to nothing else: raised when the user presses Fill form, and
  // held up through the collapse that closes the modal once the answer is in.
  const isRunning = status === 'running';
  const isBackdropUp = isRunning || status === 'filled';

  // Files dropped on the workflow pane, which is how this modal was opened in that case. Received
  // here rather than staged at the drop: the tray, the upload pipeline and the plan limit all belong
  // to this modal, so a dropped file takes exactly the path a file dropped onto the modal takes.
  useReceiveCaptureFiles({ channelSlug, onFiles: files => void addDroppedFiles(files) });

  // Two surfaces asking the same agent about the same workspace at once is a conversation the user
  // did not start. The panel is the one that goes, because Capture is what they just opened.
  useEffect(() => closeAskPanel(), [closeAskPanel]);

  useEffect(() => {
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  // Closed on success rather than left up with a confirmation: the form behind it has just been
  // filled in, and that is the thing the user needs to look at. The field goes with it, falling into
  // the middle of the screen where that form is.
  useEffect(() => {
    if (status === 'filled') {
      setIsCollapsing(true);
    }
  }, [status]);

  // Nothing unmounts until the field has finished falling into the middle of the screen. Navigating
  // on the same tick as the collapse begins would cut it off on its first frame.
  useEffect(() => {
    if (!isCollapsing) {
      return;
    }
    const { signal, abort } = getController();
    setControlledTimeout(onClose, SPARKLE_COLLAPSE_MS, signal);
    return abort;
  }, [isCollapsing, onClose]);

  // The same arrangement as the composer: speech is appended to whatever was already typed, and
  // discarding restores exactly that rather than trying to subtract the transcript back out.
  const textRef = useRef(text);
  textRef.current = text;
  const dictationBaseRef = useRef('');
  const dictatedTextRef = useRef<string | undefined>(undefined);
  const handleDictationStart = useCallback(() => {
    dictationBaseRef.current = textRef.current;
  }, []);
  const handleDictationChange = useCallback((dictated: string) => {
    const base = dictationBaseRef.current;
    dictatedTextRef.current = dictated;
    setText(isEmpty(base) ? dictated : `${base} ${dictated}`);
  }, []);
  const handleDictationEnd = useCallback((outcome: 'commit' | 'cancel') => {
    if (outcome === 'cancel') {
      setText(dictationBaseRef.current);
      dictatedTextRef.current = undefined;
    }
    dictationBaseRef.current = '';
  }, []);

  const dictation = useAskDictation({
    onStart: handleDictationStart,
    onText: handleDictationChange,
    onEnd: handleDictationEnd,
  });
  const { isActive: isDictating, finish: finishDictation } = dictation;

  const notice = captureNotice(status, isReady, isResolving);
  const isTrayFull = attachments.length >= ASK_MAX_ATTACHMENTS;
  const isAnyUploading = attachments.some(attachment => attachment.status === 'uploading');
  const hasReadyAttachment = attachments.some(attachment => attachment.status === 'ready');
  const hasWords = !isEmpty(text.trim());
  // Either is enough on its own: a photo of a nameplate says as much as a sentence about it.
  const canSubmit = (hasWords || hasReadyAttachment) && !isAnyUploading && !isRunning && isReady;

  /**
   * Keeps a drag inside the modal inside the modal.
   *
   * The dialog is portaled to `<body>`, but React sends its events up the *React* tree — which still
   * runs through the workflow pane this modal was opened from. That pane answers a dropped file by
   * offering it to the handoff bus and opening Capture, so a file dropped on the open modal was
   * attached twice: once by the listener over the body, and again by the handoff the pane made behind
   * it. Anything dropped on this modal is this modal's business.
   *
   * The default is prevented across the whole dialog, not only over the listener: an element that
   * stops doing that is treated as a non-target again, and a file dropped on the title bar becomes the
   * browser navigating away to it.
   */
  const handleModalDrag = useCallback((event: DragEvent<HTMLDialogElement>) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  /**
   * A drop on the modal's chrome — the title bar, the hint — rather than on the listener over the
   * body, which prevents the default as it attaches and so is already spoken for by the time this
   * runs. Attached the same way regardless, so the modal accepts a file anywhere on itself.
   */
  const handleModalDrop = useCallback(
    (event: DragEvent<HTMLDialogElement>) => {
      const isAlreadyAttached = event.isDefaultPrevented();
      handleModalDrag(event);
      const files = Array.from(event.dataTransfer.files);
      if (!isAlreadyAttached && !isEmpty(files)) {
        void addDroppedFiles(files);
      }
    },
    [handleModalDrag, addDroppedFiles],
  );

  // A photo takes the path a dropped file takes, rather than one of its own. `addDroppedFiles` is
  // the general staging entry point — the name is where it was first needed, not the limit of what
  // it accepts — so the content-type allowlist, the 25 MB cap, the free-plan rule and the four-file
  // ceiling are applied to a photo by exactly the code that applies them to everything else.
  const handlePhoto = useCallback((file: File) => void addDroppedFiles([file]), [addDroppedFiles]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) {
      return;
    }
    // Sending ends the dictation. The words are already in `text` — they arrive there as they are
    // spoken — so nothing about what is sent changes; it closes the microphone, which would
    // otherwise keep typing into a box the user has finished with.
    if (isDictating) {
      finishDictation();
    }
    // The dictated span goes with it, so the wire prompt can mark which words were heard rather than
    // typed. Held in a ref because the input holds one string by now and cannot say which is which.
    void run(text.trim(), attachments, dictatedTextRef.current);
  }, [canSubmit, isDictating, finishDictation, run, text, attachments]);

  return (
    <Modal
      isOpen
      pageWidth
      // Straight out, with no collapse. That send-off belongs to an answer arriving; playing it over
      // a cancelled Capture would celebrate a form that nobody filled in.
      onOpenChange={onClose}
      // `.dialog` sets `min-height: 30vh` on the bare class, which a consumer className only beats by
      // source order. Inline is the same escape hatch `AskPanel` uses, and it is what lets this modal
      // be as short as its content and grow only as the transcript and the files do.
      style={{ minHeight: 0 }}
      className={clsx(styles.captureModal, isCollapsing && styles.leaving)}
      testID="ask-capture-modal"
      aria-label={i18n('ask.capture.title')}
      // Filling a form from a photo takes as long as it takes, so the wait gets something to watch —
      // and because the field covers the create form as well as the page, it also reads as the whole
      // surface being worked on rather than just this modal. Its last act is to fall into the middle
      // of the screen, where that form is.
      backdrop={isBackdropUp ? <SparkleBackdrop isCollapsing={isCollapsing} /> : null}
      onDragEnter={handleModalDrag}
      onDragLeave={handleModalDrag}
      onDragOver={handleModalDrag}
      onDrop={handleModalDrop}
    >
      <Modal.TitleBar
        title={i18n('ask.capture.title')}
        startContent={<Modal.CloseButton onClick={onClose} testID="ask-capture-close" />}
        endContent={
          <Button
            variant="primary"
            size="large"
            label={i18n('ask.capture.submit')}
            loading={isRunning}
            disabled={!canSubmit}
            onClick={handleSubmit}
            testID="ask-capture-submit"
          />
        }
      />

      <MBFileDropListener
        onFilesDropped={addDroppedFiles}
        acceptedContentTypes={ASK_ATTACHMENT_ACCEPT}
        overlay={
          <DropTarget
            state="active"
            mode="full"
            acceptedContentTypes={ASK_ATTACHMENT_ACCEPT}
            placeholder={i18n('ask.attachments.dropHint')}
          />
        }
        // Inline, matching `AskPanel`: this listener spreads its rest props onto an `MBView`, and
        // `style` is the one that provably lands there.
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {/* Outside the body, so it spans the modal edge to edge: this is chrome continuing the title
            bar, not a card sitting inside the content. */}
        {/* A div wrapping the sentence rather than the paragraph itself: the mark renders as a div of
            its own, which inside a `<p>` would end the paragraph where it stands. */}
        <div className={styles.hint}>
          {/* Green, as the mark is on every surface where it is the thing doing the work rather than
              a control to press. Decorative: the sentence beside it already says what it means. */}
          <MBIconV2
            name={ASK_MARK_ICON}
            color={Colors.v2.green}
            size={HINT_MARK_SIZE}
            className={styles.hintMark}
          />
          <p className={styles.hintText}>{i18n('ask.capture.hint')}</p>
        </div>

        <div className={styles.body}>
          {/* Above the input rather than below it, so growing the transcript never pushes the files
              around and the two read in the order they were added. */}
          <AskAttachmentTray
            attachments={attachments}
            onRemove={removeAttachment}
            onRetry={retryAttachment}
          />

          {/* Labelled by wrapping rather than by id: the input renders a react-native-web textarea
              and forwards no id of its own. The label itself is for screen readers only, because
              the placeholder already says what this is. */}
          <label className={styles.inputWrapper}>
            <span className={styles.srOnly}>{i18n('ask.capture.transcriptLabel')}</span>
            <MBAutosizeTextInput
              ref={inputRef}
              style={{ flexGrow: 1 }}
              value={text}
              onChangeText={setText}
              placeholder={i18n('ask.capture.transcriptPlaceholder')}
              maxRows={8}
              padding="none"
            />
          </label>

          {/* Under the full-width input: controls at the right, and one slot to their left that
              everything the modal has to say shares — the meter while dictating, then whatever came
              back. Sharing it is what keeps the row from changing height as those come and go. */}
          <div className={styles.controlRow}>
            <div className={styles.feedback}>
              <AskDictationBar dictation={dictation} className={styles.dictationBar} />
              {!isEmpty(rejection) && (
                <span className={styles.rejection} role="alert" data-testid="ask-capture-rejection">
                  {rejection}
                </span>
              )}
              {/* Mounted for the life of the modal, unlike the alert above it: a live region
                  inserted into the DOM together with its text is routinely dropped, which is
                  exactly the moment a result needs announcing. */}
              <span
                className={styles.status}
                role="status"
                data-tone={notice.isProblem ? 'problem' : 'neutral'}
                data-testid="ask-capture-status"
              >
                {notice.message}
              </span>
            </div>

            <div className={styles.controls}>
              {/* Both voice paths are kept whole here too, so the switch in `voice/config.ts` moves
                  this surface and the composer together rather than leaving them disagreeing. */}
              {ASK_VOICE_INPUT_MODE === 'record' ? (
                <AskRecordButton
                  onRecorded={addRecording}
                  disabled={isRunning || isTrayFull}
                  iconSize={MICROPHONE_ICON_SIZE}
                  size="large"
                />
              ) : (
                <AskDictateButton
                  dictation={dictation}
                  disabled={isRunning}
                  iconSize={MICROPHONE_ICON_SIZE}
                  size="large"
                />
              )}
              {/* Between the two, because that is what it is: a file the user makes on the spot
                  rather than one they already have, and a way to say what is in front of them
                  without describing it. On a phone or tablet this is the shortest path there is
                  from a delivery note to a filled-in form. */}
              <AskCameraButton
                onCapture={handlePhoto}
                disabled={isRunning || isTrayFull}
                iconSize={CAMERA_ICON_SIZE}
                size="large"
              />
              <AnchoredTooltip tooltip={i18n('ask.attachments.add')} position="top">
                <Button
                  variant="ghost"
                  size="large"
                  icon="file"
                  iconOnly
                  iconSize={ATTACH_ICON_SIZE}
                  label={i18n('ask.attachments.add')}
                  disabled={isRunning || isTrayFull}
                  onClick={() => void pickAttachments()}
                  testID="ask-capture-attach"
                />
              </AnchoredTooltip>
            </div>
          </div>
        </div>
      </MBFileDropListener>
    </Modal>
  );
}

interface CaptureNotice {
  message: string;
  /** Whether this is something the user has to act on, which is what earns the red. */
  isProblem: boolean;
}

/**
 * What the modal says about itself, and in what tone.
 *
 * Exhaustive over `CaptureStatus` rather than defaulted, so a new status is a type error rather than
 * a silently blank line in a neutral colour. `empty` counts as a problem even though the run itself
 * succeeded: from where the user is standing, they added something and got nothing back.
 *
 * A workspace whose create template is still being looked up says nothing at all. It is not ready
 * either, but "Capture isn't available here" is a claim about the workspace, and on a cold cache —
 * which is every first open from the workspace header — it would be a false one for as long as the
 * query takes, corrected a moment later. The controls stay disabled meanwhile regardless.
 */
function captureNotice(
  status: CaptureStatus,
  isReady: boolean,
  isResolving: boolean,
): CaptureNotice {
  if (isResolving) {
    return { message: '', isProblem: false };
  }
  if (!isReady) {
    return { message: i18n('ask.capture.unavailable'), isProblem: true };
  }
  switch (status) {
    case 'running':
      return { message: i18n('ask.capture.running'), isProblem: false };
    case 'filled':
      return { message: i18n('ask.capture.filled'), isProblem: false };
    case 'empty':
      return { message: i18n('ask.capture.empty'), isProblem: true };
    case 'error':
      return { message: i18n('ask.capture.error'), isProblem: true };
    case 'idle':
      return { message: '', isProblem: false };
  }
}
